import { useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  ButtonBase,
  InputBase,
  Typography,
  styled,
} from '@mui/material'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import TagOutlinedIcon from '@mui/icons-material/TagOutlined'
import { Prescription } from '../../../types/models/Presc'
import { formatTimeInJP } from '../../../helpers'
import { colorTokens } from '../../../shared/styles/theme'
import { RADIUS } from '../../../shared/styles/radii'
import { LAYOUT } from '../../../shared/styles/spacing'
import {
  FONT_TABULAR,
  ICON_SIZE,
  typeScale,
} from '../../../shared/styles/typography'
import { SHORT_SERVICE_NAMES } from '../../../constants/const'
import { BrandMark } from '../../medilab/latest/BrandMark'
import {
  foldByOcrRequestEvent,
  getPatientName,
  groupByDate,
  isQrOnly,
  matchesSearch,
} from '../../medilab/latest/list/queue'
import { AnnotatorPharmacy } from '../apis/annotatorView'
import {
  FormatStatus,
  formatStatusLabel,
  fullHospitalCodeOf,
} from './formatSetting/hospitalCode'

/**
 * annotator の閲覧（latest UI）の Sidebar。**できるのは 2 つだけ**（松田 9/2）:
 *
 * 1. 薬局を切り替える
 * 2. リクエストIDで開く
 *
 * リクエストID 欄は「コマンド」で、身元の置き場ではない（開いている紙の ID は右ペインの tablist 行に出る。
 * log/62 §2 A/B）。**開けたら空にする**（legacy と同じ）・focus で全選択（失敗して文字が残っていても
 * 「クリック → 貼る」で置き換わる）・失敗は文字を残して 1 行注記。クリアボタンは置かない。
 *
 * 顧客側 apps/medilab/latest/Sidebar とは別物（あちらは usePrescriptionsQuery を内に持ち
 * 自店舗に閉じている）だが、**面の作法は正典どおり同じもの**にする:
 * WorkspaceRow(02-components/sidebar.md) / 3 本のフィルタ行を共通枠で揃える(04-apps/kakeru.md
 * HomePane の作法) / 行は StatusDot + 患者名 + 時刻 / 日付見出しで区切る(04-apps/mamoru.md)。
 *
 * **「どの店を見ているか」は薬局セレクタ自身が持つ。**そのための帯を別に立てない
 * (00-principles/03-subtraction.md・TopBar を置かない規則)。
 */

/**
 * フィルタ行の共通枠。3 本（薬局 / リクエストID / 患者名）を 1 つの枠に揃える
 * (04-apps/kakeru.md「フィルタ行を共通枠 FILTER_ROW_SX で統一」)。
 * 左アイコンは left 8px / 14px、入力の左 padding は 26px (= 8 + 14 + 4) で x 座標を揃える。
 *
 * **⚠ sx の borderRadius に数値を渡さない。**MUI は数値を `theme.shape.borderRadius` 倍する。
 * このアプリは shape=RADIUS.md(6) なので `borderRadius: RADIUS.lg`(8) は 8×6=48px になり、
 * 高さ 28px の枠が完全な丸になる。**px 文字列で渡すと素通しになる**ので必ずそうする。
 * styled() の素の CSS オブジェクトは倍されないので、そちらは数値のままでよい。
 */
const FILTER_ROW_SX = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  height: 28,
  borderRadius: `${RADIUS.lg}px`,
  border: `1px solid ${colorTokens.border.default}`,
  background: colorTokens.bg.default,
} as const

const FILTER_ICON_SX = {
  position: 'absolute',
  left: 8,
  fontSize: ICON_SIZE.sm,
  color: colorTokens.fg.faint,
  pointerEvents: 'none',
} as const

const FILTER_INPUT_SX = {
  width: '100%',
  height: '100%',
  pl: '26px',
  pr: 1,
  fontSize: 12,
  color: colorTokens.fg.default,
} as const

const Container = styled(Box)({
  width: LAYOUT.sidebarWidth,
  flexShrink: 0,
  background: colorTokens.bg.subtle,
  borderRight: `1px solid ${colorTokens.border.default}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'sticky',
  top: 0,
  // 折りたたみトグルは hover でだけ出す(03-patterns/hover-only-chrome.md)
  '&:hover .collapse-toggle': { opacity: 1 },
})

const WorkspaceRow = styled(Box)({
  height: LAYOUT.sidebarWorkspaceRowHeight,
  padding: '0 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 13,
  fontWeight: 600,
  color: colorTokens.fg.default,
})

const RowList = styled('ul')({ listStyle: 'none', margin: 0, padding: 0 })

const Row = styled('li', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: LAYOUT.sidebarRowHeight,
  padding: '0 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  background: selected ? colorTokens.bg.emphasis : 'transparent',
  '&:hover': {
    background: selected ? colorTokens.bg.emphasis : colorTokens.overlay.hover,
  },
}))

/**
 * 行頭 8px ドット。**色相を使わず塗り / 輪郭の二値**で示す
 * (00-principles/04-color-discipline.md §4)。突合の対象 = 塗り / QR 直読 = 輪郭。
 */
const Dot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'filled',
})<{ filled: boolean }>(({ filled }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  ...(filled
    ? { background: colorTokens.fg.faint }
    : { border: `1px solid ${colorTokens.fg.disabled}` }),
}))

const FooterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: LAYOUT.sidebarRowHeight,
  margin: '0 8px 8px',
  padding: '0 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  fontSize: 13,
  color: colorTokens.fg.muted,
  '&:hover': { background: colorTokens.bg.emphasis },
})

/** 一覧が全部でないことの一言。出る条件のときだけ描く(件数は常設しない)。 */
const Notice = ({ children }: { children: string }) => (
  <Typography
    sx={{ px: 2, pb: 0.5, ...typeScale.tag, color: colorTokens.fg.muted }}
  >
    {children}
  </Typography>
)

const PrescriptionRow = ({
  presc,
  formatLabel,
  isSelected,
  onSelect,
}: {
  presc: Prescription
  /** 様式の判断がまだなら「様式未設定」等。無ければ null（何も出さない） */
  formatLabel: string | null
  isSelected: boolean
  onSelect: (ocrRequestEventId: string) => void
}) => {
  const qrOnly = isQrOnly(presc)

  return (
    <Row
      selected={isSelected}
      onClick={() => onSelect(presc.ocr_request_event_id)}
    >
      <Dot filled={!qrOnly} />
      <Typography
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          color: qrOnly ? colorTokens.fg.muted : colorTokens.fg.default,
        }}
      >
        {getPatientName(presc)}
      </Typography>
      {/* 様式が未判断の紙に一言。無彩色の素のテキストで、色も囲みも使わない（重要度を色で出さない） */}
      {formatLabel && (
        <Typography
          noWrap
          sx={{ ...typeScale.tag, color: colorTokens.fg.faint, flexShrink: 0 }}
        >
          {formatLabel}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: 12,
          ...FONT_TABULAR,
          color: colorTokens.fg.muted,
          flexShrink: 0,
        }}
      >
        {formatTimeInJP(presc.request_event_created_at)}
      </Typography>
    </Row>
  )
}

interface Props {
  pharmacies: AnnotatorPharmacy[]
  /** 候補が上限で打ち切られた。「候補が無い」と取り違えさせない */
  pharmaciesHasMore: boolean
  /** 一覧が全部でないときの一言（打ち切り・描けなかった行）。無ければ null */
  listNotice: string | null
  pharmacyKeyword: string
  onPharmacyKeywordChange: (keyword: string) => void
  selectedPharmacy: AnnotatorPharmacy | null
  onSelectPharmacy: (pharmacy: AnnotatorPharmacy | null) => void
  prescriptions: Prescription[]
  /** 統一医療機関コード → 様式の判断済みフラグ（一覧レスポンスの format_status） */
  formatStatus: Record<string, FormatStatus>
  isListPending: boolean
  selectedId: string | null
  onSelect: (ocrRequestEventId: string) => void
  /** 開けたら true。true のとき欄を空にする */
  onLookupRequestId: (requestId: string) => Promise<boolean>
  requestIdError: string
  isLookingUp: boolean
  onCollapse: () => void
}

export const Sidebar = ({
  pharmacies,
  pharmaciesHasMore,
  listNotice,
  pharmacyKeyword,
  onPharmacyKeywordChange,
  selectedPharmacy,
  onSelectPharmacy,
  prescriptions,
  formatStatus,
  isListPending,
  selectedId,
  onSelect,
  onLookupRequestId,
  requestIdError,
  isLookingUp,
  onCollapse,
}: Props) => {
  const [patientSearch, setPatientSearch] = useState('')
  const [requestIdInput, setRequestIdInput] = useState('')

  const rows = useMemo(
    () => foldByOcrRequestEvent(prescriptions),
    [prescriptions]
  )
  const filtered = useMemo(
    () => rows.filter((presc) => matchesSearch(presc, patientSearch)),
    [rows, patientSearch]
  )
  const groups = useMemo(() => groupByDate(filtered), [filtered])

  // リクエストID 欄の下に出す一言。探索中・見つからないの 2 つだけ。
  const requestIdNote = isLookingUp ? '探しています' : requestIdError

  return (
    <Container>
      <WorkspaceRow>
        <BrandMark />
        <Typography
          component="span"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}
        >
          {SHORT_SERVICE_NAMES.DEFAULT}
        </Typography>
        {/*
          段階ラベル(02-components/sidebar.md)。**囲みなしの素のテキスト**で、
          クリック領域の外に置く。読み取り専用であることをこの 1 語が持つので、
          面の上に「閲覧のみ」の帯を別に立てない。
        */}
        <Typography
          component="span"
          sx={{ flexShrink: 0, ...typeScale.tag, color: colorTokens.fg.muted }}
        >
          閲覧
        </Typography>
        <ButtonBase
          className="collapse-toggle"
          aria-label="一覧を格納する"
          onClick={onCollapse}
          sx={{
            width: 22,
            height: 22,
            borderRadius: `${RADIUS.sm}px`,
            opacity: 0,
            transition: 'opacity 120ms',
            color: colorTokens.fg.muted,
            '&:hover': { background: colorTokens.bg.emphasis },
            '&:focus-visible': { opacity: 1 },
          }}
        >
          <KeyboardDoubleArrowLeftIcon sx={{ fontSize: ICON_SIZE.sm }} />
        </ButtonBase>
      </WorkspaceRow>

      <Box
        sx={{
          px: 1,
          pb: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Autocomplete
          options={pharmacies}
          value={selectedPharmacy}
          onChange={(_, next) => onSelectPharmacy(next)}
          inputValue={pharmacyKeyword}
          onInputChange={(_, next) => onPharmacyKeywordChange(next)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          // 絞り込みは backend が店舗名と法人名の両方に当てている。手元で二重に絞らない
          filterOptions={(options) => options}
          noOptionsText="該当なし"
          slotProps={{
            paper: {
              sx: {
                borderRadius: `${RADIUS.lg}px`,
                boxShadow: colorTokens.shadow.popover,
                '& .MuiAutocomplete-noOptions': {
                  fontSize: 12,
                  color: colorTokens.fg.faint,
                },
              },
            },
            listbox: { sx: { py: 0.5 } },
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13 }}>
                  {option.name}
                </Typography>
                <Typography
                  noWrap
                  sx={{ ...typeScale.tag, color: colorTokens.fg.faint }}
                >
                  {option.company_name}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <Box ref={params.InputProps.ref} sx={FILTER_ROW_SX}>
              <StorefrontOutlinedIcon sx={FILTER_ICON_SX} />
              <InputBase
                inputProps={{
                  ...params.inputProps,
                  'aria-label': '薬局を選ぶ',
                }}
                placeholder="薬局を選ぶ"
                sx={FILTER_INPUT_SX}
              />
            </Box>
          )}
        />

        {pharmaciesHasMore && (
          <Typography sx={{ ...typeScale.tag, color: colorTokens.fg.faint }}>
            候補が多いため一部のみ表示。絞り込んでください
          </Typography>
        )}

        <Box sx={FILTER_ROW_SX}>
          <TagOutlinedIcon sx={FILTER_ICON_SX} />
          <InputBase
            value={requestIdInput}
            onChange={(event) => setRequestIdInput(event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void onLookupRequestId(requestIdInput).then((opened) => {
                  if (opened) setRequestIdInput('')
                })
              }
            }}
            placeholder="リクエストIDで開く"
            inputProps={{ 'aria-label': 'リクエストIDで開く' }}
            sx={FILTER_INPUT_SX}
          />
        </Box>

        {requestIdNote && (
          <Typography sx={{ ...typeScale.tag, color: colorTokens.fg.faint }}>
            {requestIdNote}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ ...FILTER_ROW_SX, flex: 1 }}>
            <SearchOutlinedIcon sx={FILTER_ICON_SX} />
            <InputBase
              value={patientSearch}
              onChange={(event) => setPatientSearch(event.target.value)}
              placeholder="患者名で絞り込む"
              inputProps={{ 'aria-label': '患者名で絞り込む' }}
              sx={FILTER_INPUT_SX}
            />
          </Box>
          {/* 件数は検索がアクティブな時だけ(03-patterns/list-and-status.md) */}
          {patientSearch.trim() && (
            <Typography
              sx={{ ml: 1, ...typeScale.tag, color: colorTokens.fg.muted }}
            >
              {filtered.length}
            </Typography>
          )}
        </Box>
      </Box>

      {listNotice && <Notice>{listNotice}</Notice>}

      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {groups.map((group) => (
          <Box key={group.key} sx={{ mt: 1.5 }}>
            <Typography
              sx={{
                px: 1,
                mb: 0.25,
                fontSize: 12,
                color: colorTokens.fg.faint,
              }}
            >
              {group.label}
            </Typography>
            <RowList>
              {group.rows.map((presc) => (
                <PrescriptionRow
                  key={presc.ocr_request_event_id}
                  presc={presc}
                  formatLabel={formatStatusLabel(
                    formatStatus[fullHospitalCodeOf(presc) ?? '']
                  )}
                  isSelected={presc.ocr_request_event_id === selectedId}
                  onSelect={onSelect}
                />
              ))}
            </RowList>
          </Box>
        ))}

        {/* empty-states.md 型1。段落文にしない */}
        {!selectedPharmacy && (
          <Typography
            sx={{ px: 1, py: 1.5, fontSize: 13, color: colorTokens.fg.faint }}
          >
            薬局を選んでください
          </Typography>
        )}
        {selectedPharmacy && !isListPending && groups.length === 0 && (
          <Typography
            sx={{ px: 1, py: 1.5, fontSize: 13, color: colorTokens.fg.faint }}
          >
            (なし)
          </Typography>
        )}
      </Box>

      <FooterRow onClick={() => window.location.assign('/portal')}>
        設定 / ポータルを開く
      </FooterRow>
    </Container>
  )
}
